import { defineNuxtPlugin } from '@nuxtjs/composition-api';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_STYLESHEET = 'https://assets.calendly.com/assets/external/widget.css';
const CALENDLY_SCRIPT_ID = 'calendly-script';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: ({ url }: { url: string }) => void;
      closePopupWidget: () => void;
    };
  }
}

export enum CalendlyEvent {
  dateSelected = 'calendly-date-and-time-selected',
  eventScheduled = 'calendly-event-scheduled',
  typeViewed = 'calendly-event-type-viewed',
  profilePageViewed = 'calendly-profile-page-viewed',
}

export interface NuxtCalendlyPlugin {
  injectScript: () => Promise<void>;
  open: (paylod: { url: string; prefill?: { name: string; email: string } }) => void;
  close: () => void;
  registerCalendlyEventListener: (cb: (e: MessageEvent<any>) => void) => () => void;
}

export default defineNuxtPlugin((_context, inject) => {
  const injectScript = () => {
    const verifyIfScriptWasInjected = () => !!document.querySelector(`#${CALENDLY_SCRIPT_ID}`);

    return new Promise((resolve, reject) => {
      if (verifyIfScriptWasInjected()) return resolve(true);

      const calendlyScript = document.createElement('script');
      calendlyScript.setAttribute('src', CALENDLY_SCRIPT);
      calendlyScript.setAttribute('id', CALENDLY_SCRIPT_ID);
      calendlyScript.onload = () => resolve(true);
      calendlyScript.onerror = reject;
      document.head.appendChild(calendlyScript);

      const calendlyLink = document.createElement('link');
      calendlyLink.setAttribute('href', CALENDLY_STYLESHEET);
      calendlyLink.setAttribute('rel', 'stylesheet');
      document.head.appendChild(calendlyLink);
    });
  };

  const open = (payload: { url: string; prefill?: { name: string; email: string } }) => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget(payload);
    }
  };

  const close = () => {
    if (window.Calendly) {
      window.Calendly.closePopupWidget();
    }
  };

  const isCalendlyEvent = (e: MessageEvent<any>) => {
    return e.data.event && e.data.event.indexOf('calendly') === 0;
  };

  const registerCalendlyEventListener = (cb: (e: MessageEvent<any>) => Promise<void>) => {
    const listener = (e: MessageEvent<any>) => {
      if (isCalendlyEvent(e)) {
        return cb(e);
      }
    };

    window.addEventListener('message', listener);

    return () => window.removeEventListener('message', listener);
  };

  inject('calendly', {
    injectScript,
    open,
    close,
    registerCalendlyEventListener,
  });
});
